import { useEffect, useRef, useState } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { disposeObject3D, isAbortError, loadModelGroup } from "../lib/scanModelLoader";
import { createScanViewerStage } from "../lib/scanViewerStage";

const CONTEXT_LOST_MESSAGE =
  "The browser ran out of graphics memory showing this model, so the view stopped. " +
  "Pick a lighter package above (for example \"On-device preview (textured mesh)\"), " +
  "or close other tabs and open the scan again.";

function fitCameraToObject(camera, controls, object) {
  const box = new THREE.Box3().setFromObject(object);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z, 0.001);
  const distance = maxDim * 2.2;
  camera.position.set(center.x + distance, center.y + distance * 0.6, center.z + distance);
  camera.near = Math.max(maxDim / 200, 0.01);
  camera.far = Math.max(distance * 20, 100);
  camera.updateProjectionMatrix();
  controls.target.copy(center);
  controls.update();
}

function UsdzFallback({ src, label }) {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        padding: 24,
        textAlign: "center",
        background: "#111318",
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text-1)" }}>
        {label || "USDZ model"}
      </div>
      <div style={{ fontSize: 12, color: "var(--text-3)", maxWidth: 360, lineHeight: 1.5 }}>
        Inline USDZ preview is not supported in the CRM viewer (same as the client portal 3D tab).
        Open or download the file below — Safari supports AR Quick Look.
      </div>
      <a
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-sm btn-primary"
        style={{ textDecoration: "none" }}
      >
        Open USDZ
      </a>
    </div>
  );
}

export default function ScanModelViewer({ scanId, viewer, onClose }) {
  const shellRef = useRef(null);
  const mountRef = useRef(null);
  const rendererRef = useRef(null);
  const frameRef = useRef(null);
  const resizeRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("Preparing viewer…");
  const [error, setError] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [usdzSrc, setUsdzSrc] = useState("");
  const [displayLabel, setDisplayLabel] = useState(viewer?.mesh_filename || "Scan preview");
  const [useUsdz, setUseUsdz] = useState(false);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === shellRef.current);
      resizeRef.current?.();
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  async function toggleFullscreen() {
    const el = shellRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement === el) {
        await document.exitFullscreen();
      } else {
        await el.requestFullscreen();
      }
    } catch (_) {
      /* browser may block */
    }
  }

  useEffect(() => {
    if (!viewer?.mesh_url && !viewer?.mesh_file_id) return undefined;

    let disposed = false;
    let cleanupThree = null;
    // Closing the modal or picking another package cancels this load's
    // downloads and decodes (a ReconKit model is up to ~130 MB + 32 atlases).
    const controller = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setError("");
        setUseUsdz(false);
        setUsdzSrc("");
        const loaded = await loadModelGroup({
          scanId,
          viewer,
          signal: controller.signal,
          onStep: (text) => {
            if (!disposed) setStep(text);
          },
        });
        if (disposed) {
          if (loaded.kind === "three") disposeObject3D(loaded.object);
          return;
        }

        if (loaded.kind === "usdz") {
          setUsdzSrc(loaded.blobUrl);
          setDisplayLabel(loaded.label || viewer.mesh_filename || "USDZ preview");
          setUseUsdz(true);
          setLoading(false);
          return;
        }

        setDisplayLabel(loaded.label || viewer.mesh_filename || "Scan preview");
        setUseUsdz(false);

        const mount = mountRef.current;
        if (!mount) throw new Error("Viewer mount missing");

        // Scene, lights, background and the hole blur: scanViewerStage.js.
        const stage = createScanViewerStage({ pixelRatio: Math.min(window.devicePixelRatio || 1, 2) });
        const { renderer, scene } = stage;
        const camera = new THREE.PerspectiveCamera(50, mount.clientWidth / mount.clientHeight, 0.01, 5000);
        stage.setSize(mount.clientWidth, mount.clientHeight);
        mount.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.08;

        scene.add(loaded.object);
        fitCameraToObject(camera, controls, loaded.object);

        const animate = () => {
          frameRef.current = requestAnimationFrame(animate);
          controls.update();
          stage.render(camera);
        };
        animate();

        // Texture bitmaps are released after their GPU upload, so a restored
        // context could not be refilled: stop and say what happened.
        const onContextLost = () => {
          if (frameRef.current) cancelAnimationFrame(frameRef.current);
          frameRef.current = null;
          if (!disposed) setError(CONTEXT_LOST_MESSAGE);
        };
        renderer.domElement.addEventListener("webglcontextlost", onContextLost);

        const onResize = () => {
          if (!mount) return;
          const w = mount.clientWidth;
          const h = mount.clientHeight;
          camera.aspect = w / Math.max(h, 1);
          camera.updateProjectionMatrix();
          stage.setSize(w, h);
        };
        resizeRef.current = onResize;
        window.addEventListener("resize", onResize);
        const resizeObserver = new ResizeObserver(onResize);
        resizeObserver.observe(mount);

        cleanupThree = () => {
          renderer.domElement.removeEventListener("webglcontextlost", onContextLost);
          window.removeEventListener("resize", onResize);
          resizeObserver.disconnect();
          if (frameRef.current) cancelAnimationFrame(frameRef.current);
          controls.dispose();
          stage.dispose();
          if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
          disposeObject3D(scene);
        };

        setLoading(false);
      } catch (e) {
        if (!disposed && !isAbortError(e)) {
          setError(e?.message || String(e));
          setLoading(false);
        }
      }
    })();

    return () => {
      disposed = true;
      controller.abort();
      resizeRef.current = null;
      rendererRef.current = null;
      cleanupThree?.();
    };
  }, [scanId, viewer]);

  return (
    <div className="scan-viewer-shell" ref={shellRef}>
      <div className="scan-viewer-toolbar">
        <div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>{displayLabel}</div>
          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
            Drag to orbit · scroll to zoom
          </div>
        </div>
        <div className="scan-viewer-toolbar-actions">
          <button
            type="button"
            className="btn btn-sm"
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            <span>{isFullscreen ? "Exit full screen" : "Full screen"}</span>
          </button>
          <button type="button" className="btn btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      <div className="scan-viewer-canvas-wrap" ref={mountRef}>
        {useUsdz && usdzSrc ? <UsdzFallback src={usdzSrc} label={displayLabel} /> : null}
        {loading ? (
          <div className="scan-viewer-overlay">
            <div className="scan-viewer-spinner" />
            <div style={{ fontSize: 13, color: "var(--text-2)" }}>{step}</div>
          </div>
        ) : null}
        {error ? (
          <div className="scan-viewer-overlay scan-viewer-overlay--error">
            <div style={{ fontSize: 13, color: "var(--red)", maxWidth: 420, textAlign: "center", lineHeight: 1.5 }}>
              {error}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
